import { Bar } from "react-chartjs-2";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  LinearScale,
  Tooltip,
} from "chart.js";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import useApi from "../../../helpers/apiHelper/requestHelper";

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip);

const formatDate = (dateString) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  const day = String(date.getUTCDate()).padStart(2, "0");
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const year = date.getUTCFullYear();
  return `${day}-${month}-${year}`;
};

const CommunityBarChart = ({ duration, graphType, userId,dottedLines=true }) => {
  const [dataValues, setDataValues] = useState(Array(14).fill(0));
  const [labels, setLabels] = useState(Array(14).fill(""));
  const [isServiceStartData, setIsServiceStartData] = useState(true);
  const user = useSelector(state => state?.user?.user);

  const barThickness = window.innerWidth < 576 ? 10 : 18;
  const chartRef = useRef(null);
  const makeApiCall = useApi();
  const weekRangesRef = useRef([]);



  // Function to get service start date label based on duration using user's createdAt
  const getServiceStartLabel = (duration, createdAt) => {
    if (!createdAt) {
      // Fallback to current date if createdAt is not available
      const now = new Date();
      return duration.toLowerCase() === "month" ? now.toLocaleDateString('en-US', { month: 'long' }) :
             duration.toLowerCase() === "week" ? "1" :
             formatDate(now);
    }

    const createdDate = new Date(createdAt);
    
    switch (duration.toLowerCase()) {
      case "month":
        return createdDate.toLocaleDateString('en-US', { month: 'long' }); // e.g., "January"
      case "week":
        // Get week number of the year when user was created
        const firstDayOfYear = new Date(createdDate.getFullYear(), 0, 1);
        const pastDaysOfYear = (createdDate - firstDayOfYear) / 86400000;
        const weekNumber = Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);
        return weekNumber.toString(); // e.g., "1", "2", etc.
      case "all time":
      default:
        return formatDate(createdDate);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      const produration = duration.toLowerCase();
      
      try {
        const json = await makeApiCall(
          "get",
          `historyGraph?duration=${produration}&graphType=${graphType}&userId=${userId}`,
          {
           duration:produration,
            graphType,
            userId,
          }
        );
        
        

        if (json?.status && Array.isArray(json.data)) {
          const responseData = json.data;

          // Monthly: always show all 12 months of current year
          if (duration.toLowerCase() === "month") {
            setIsServiceStartData(false);
            const monthNames = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
            const currentYear = new Date().getFullYear();
            const dataMap = {};
            responseData.forEach((item) => {
              const match = item.key && item.key.match(/^([A-Za-z]+)\s+(\d{4})/);
              if (match) {
                const idx = new Date(`${match[1]} 1, ${match[2]}`).getMonth();
                if (parseInt(match[2]) === currentYear) dataMap[idx] = item.value;
              }
            });
            setDataValues(monthNames.map((_, i) => dataMap[i] ?? 0));
            setLabels(monthNames);
            return;
          }
             const hasInsufficientData = responseData.length ===0 ||
            responseData.every(item => item.value === 0 || item.value === null || item.value === undefined);
          
            if (hasInsufficientData) {

              // Show service start data - single bar
              setIsServiceStartData(true);
              
              // Create single bar data - X-axis will show "0"
              const startPosition = 0;
              const newDataValues = Array(14).fill(0);
              // If the real value is 0 / no data, don't force a visible bar.
              // Use 0 (invisible) instead of 1 (visible).
              newDataValues[startPosition] = 0;
              
              const newLabels = Array(14).fill("");
              newLabels[startPosition] = "0"; // Directly set "0" as X-axis label
              
              setDataValues(newDataValues);
              setLabels(newLabels);
            }else {
            // Normal data processing - data should start from left to right
            setIsServiceStartData(false);

            // Filter out entries that are truly empty (no key or null/undefined values)
            // Keep entries with valid keys even if value is 0 (legitimate zero values)
            const actualData = responseData.filter(item =>
              item.key !== "" && item.key !== null && item.key !== undefined &&
              (item.value !== null && item.value !== undefined)
            );

            // For "day" duration, show all days of the current month (future days = 0)
            if (duration.toLowerCase() === "day") {
              const dataMap = {};
              actualData.forEach((item) => {
                const match = item.key && item.key.match(/(\d{4}-\d{2}-\d{2})/);
                if (match) dataMap[match[1]] = item.value;
              });

              const today = new Date();
              const year = today.getUTCFullYear();
              const month = today.getUTCMonth();
              const daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();

              const allDays = [];
              for (let day = 1; day <= daysInMonth; day++) {
                const key = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
                allDays.push({ key, value: dataMap[key] ?? 0, label: String(day) });
              }

              setDataValues(allDays.map((d) => d.value));
              setLabels(allDays.map((d) => d.label));
              return;
            }

            // For "week" duration, show all weeks of the current year up to current week
            if (duration.toLowerCase() === "week") {
              const dataMap = {};
              actualData.forEach((item) => {
                const match = item.key && item.key.match(/Week (\d+)/);
                if (match) dataMap[parseInt(match[1])] = item.value;
              });

              const now = new Date();
              const d = new Date(now);
              d.setHours(0, 0, 0, 0);
              d.setDate(d.getDate() + 4 - (d.getDay() || 7));
              const yearStart = new Date(d.getFullYear(), 0, 1);
              const currentWeek = Math.ceil(((d - yearStart) / 86400000 + 1) / 7);

              const year = now.getFullYear();
              const jan4 = new Date(year, 0, 4);
              const jan4Day = jan4.getDay() || 7;
              const week1Monday = new Date(jan4);
              week1Monday.setDate(jan4.getDate() - (jan4Day - 1));

              const allWeeks = [];
              const ranges = [];
              for (let w = 1; w <= currentWeek; w++) {
                const monday = new Date(week1Monday);
                monday.setDate(week1Monday.getDate() + (w - 1) * 7);
                const sunday = new Date(monday);
                sunday.setDate(monday.getDate() + 6);
                const fmt = (d) => `${String(d.getDate()).padStart(2,"0")}/${String(d.getMonth()+1).padStart(2,"0")}`;
                ranges.push(`${fmt(monday)} - ${fmt(sunday)}`);
                allWeeks.push({ label: String(w), value: dataMap[w] ?? 0 });
              }
              weekRangesRef.current = ranges;

              setDataValues(allWeeks.map((w) => w.value));
              setLabels(allWeeks.map((w) => w.label));
              return;
            }

            // Take the last 14 items from actual data (most recent data)
            const last14 = actualData.slice(-14);

            // Pad at the end (right side) instead of beginning (left side)
            const filled = last14.concat(
              Array(14 - last14.length).fill({ key: "", value: 0 })
            );

            setDataValues(filled.map((d) => d.value));
            setLabels(
              filled.map((d) => {
                if (duration.toLowerCase() === "month") {
                  const match = d.key.match(/^([A-Za-z]+)/);
                  return match ? match[1] : "";
                } else if (duration.toLowerCase() === "week") {
                  const match = d.key.match(/Week (\d+)/);
                  return match ? match[1] : "";
                } else if (duration.toLowerCase() === "all time") {
                  const match = d.key.match(/(\d{4})-(\d{2})-(\d{2})/);
                  return match 
                    ? `${match[3]}-${match[2]}-${match[1]}` 
                    : "";
                }
                return "";
              })
            );
          }
        } else {
          // No data received - show service start data
          setIsServiceStartData(true);
          
          const serviceStartLabel = getServiceStartLabel(duration, user?.createdAt);
          const singleBarValue = 0;
          
          // Place bar at the start (left side) - position 0
          const startPosition = 0;
          const newDataValues = Array(14).fill(0);
          newDataValues[startPosition] = singleBarValue;
          
          const newLabels = Array(14).fill("");
          newLabels[startPosition] = serviceStartLabel;
          
          setDataValues(newDataValues);
          setLabels(newLabels);
        }
      } catch (e) {
        console.error("Error fetching chart data:", e);
        
        // Error case - show service start data
        setIsServiceStartData(true);
        
        const serviceStartLabel = getServiceStartLabel(duration, user?.createdAt);
        const singleBarValue = 0;
        
        // Place bar at the start (left side) - position 0
        const startPosition = 0;
        const newDataValues = Array(14).fill(0);
        newDataValues[startPosition] = singleBarValue;
        
        const newLabels = Array(14).fill("");
        newLabels[startPosition] = serviceStartLabel;
        
        setDataValues(newDataValues);
        setLabels(newLabels);
      }
    };

    fetchData();
  }, [duration, graphType, userId]);

  const data = {
    datasets: [
      {
        backgroundColor: (ctx) => {
          return dataValues[ctx.dataIndex] ? "#11b700" : "#dfe3ea";
        },
        barThickness,
        borderRadius: 50,
        borderSkipped: false,
        data: dataValues,
        label: isServiceStartData ? "Community Started" : "Revenue",
      },
    ],
    labels,
  };

  const externalTooltipHandler = (context) => {
    const { chart, tooltip } = context;
    const container = chart.canvas.parentNode;

    let tooltipEl = container.querySelector(".custom-tooltip");

    if (!tooltipEl) {
      tooltipEl = document.createElement("div");
      tooltipEl.className = "custom-tooltip";
      tooltipEl.style.pointerEvents = "none";
      tooltipEl.style.position = "absolute";
      tooltipEl.style.transition = "all 0.1s ease";
      tooltipEl.style.zIndex = "1000";
      container.appendChild(tooltipEl);
    }

    if (tooltip.opacity === 0) {
      tooltipEl.style.opacity = 0;
      return;
    }

    const value = tooltip.dataPoints?.[0]?.raw ?? "";

    // Different tooltip content for service start data
    let tooltipContent;
    if (isServiceStartData) {
      const createdDate = formatDate(user?.createdAt);
      tooltipContent = `Service Started: ${createdDate}`;
    } else {
      tooltipContent = graphType === "subscribers"
        ? value.toLocaleString()
        : graphType === "growth"
        ? `${value.toLocaleString()}%`
        : `$${value.toLocaleString()}`;
    }

    tooltipEl.innerHTML = `
      <div style="
        background: #11b700;
        border-radius: 20px;
        color: white;
        font-size: 14px;
        font-weight: bold;
        padding: 6px 14px;
        position: relative;
        text-align: center;
        white-space: nowrap;
      ">
        ${tooltipContent}
        <div style="
          border-left: 6px solid transparent;
          border-right: 6px solid transparent;
          border-top: 6px solid #11b700;
          bottom: -6px;
          height: 0;
          left: 50%;
          position: absolute;
          transform: translateX(-50%);
          width: 0;
        "></div>
      </div>
    `;

    const canvas = chart.canvas;
    const scaleX = canvas.offsetWidth / canvas.width;
    const scaleY = canvas.offsetHeight / canvas.height;
    const tooltipX = tooltip.caretX * scaleX;
    const tooltipY = tooltip.caretY * scaleY;

    tooltipEl.style.left = `${tooltipX - tooltipEl.offsetWidth / 2}px`;
    tooltipEl.style.opacity = 1;
    tooltipEl.style.top = `${tooltipY - tooltipEl.offsetHeight - 12}px`;
  };

  useEffect(() => {
    const chartContainer = chartRef.current?.canvas?.parentNode;

    return () => {
      if (chartContainer) {
        const el = chartContainer.querySelector(".custom-tooltip");
        if (el) el.remove();
      }
    };
  }, []);

  useEffect(() => {
    if (duration.toLowerCase() !== "week") return;
    const canvas = chartRef.current?.canvas;
    if (!canvas) return;

    let tickTooltipEl = null;

    const getOrCreate = () => {
      if (!tickTooltipEl) {
        tickTooltipEl = document.createElement("div");
        tickTooltipEl.style.cssText = `
          position:absolute;background:#333;color:#fff;font-size:11px;
          padding:4px 8px;border-radius:6px;pointer-events:none;
          white-space:nowrap;opacity:0;transition:opacity 0.15s ease;z-index:999;
        `;
        canvas.parentNode.appendChild(tickTooltipEl);
      }
      return tickTooltipEl;
    };

    const onMouseMove = (e) => {
      const chart = chartRef.current;
      if (!chart) return;
      const xScale = chart.scales?.x;
      if (!xScale || !weekRangesRef.current.length) return;
      const rect = canvas.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;
      if (mouseY < xScale.top) { if (tickTooltipEl) tickTooltipEl.style.opacity = 0; return; }
      let found = false;
      for (let i = 0; i < xScale.ticks.length; i++) {
        const tickX = xScale.getPixelForTick(i);
        if (Math.abs(mouseX - tickX) < 16) {
          const range = weekRangesRef.current[i];
          if (range) {
            const el = getOrCreate();
            el.textContent = range;
            el.style.left = `${tickX - el.offsetWidth / 2}px`;
            el.style.top = `${xScale.top - 34}px`;
            el.style.opacity = 1;
            found = true;
          }
          break;
        }
      }
      if (!found && tickTooltipEl) tickTooltipEl.style.opacity = 0;
    };

    const onMouseLeave = () => { if (tickTooltipEl) tickTooltipEl.style.opacity = 0; };

    canvas.addEventListener("mousemove", onMouseMove);
    canvas.addEventListener("mouseleave", onMouseLeave);
    return () => {
      canvas.removeEventListener("mousemove", onMouseMove);
      canvas.removeEventListener("mouseleave", onMouseLeave);
      if (tickTooltipEl) tickTooltipEl.remove();
    };
  }, [duration, labels]);

  const options = {
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { enabled: false, external: externalTooltipHandler },
    },
    responsive: true,
    scales: {
      x: {
        grid: { display: false },
        ticks: { 
          color: "#06152b", 
          font: { size: 14, weight: "500" },
          // Hide empty labels for cleaner look
          callback: function(value, index) {
            return this.getLabelForValue(value) || "";
          }
        },
      },
      y: {
        grid:{ borderDash: [4, 4], color: "rgba(46, 107, 199, 0.5)" } ,
  //       grid:dottedLines 
  // ? { borderDash: [4, 4], color: "rgba(46, 107, 199, 0.5)" } 
  // : { display: false },
        ticks: {
          callback: (value) => {
            if (isServiceStartData) {
              return ""; // Hide y-axis values for service start data
            }
            return graphType === "subscribers"
              ? value.toLocaleString()
              : graphType === "growth"
              ? `${value.toLocaleString()}%`
              : `$${value.toLocaleString()}`;
          },
          color: "#727272",
          font: { size: 12 },
        },
        // Set appropriate min/max for service start data
        min: isServiceStartData ? 0 : undefined,
        max: isServiceStartData ? 2 : undefined,
      },
    },
  };

  return (
    <div
      className="chart-container graph__chart"
      style={{ height: "100%", position: "relative", width: "100%" }}
    >
      <Bar data={data} options={options} ref={chartRef} />
    </div>
  );
};

export default CommunityBarChart;